import { BookingParams, BookingPosition, BookingResult, } from '../types';
import { Log } from '../util/log';

/**
 * Basis für Action-Implementierungen mit Hilfsmethoden für Result-Konstruktion.
 * Subklassen implementieren nur `execute()`.
 */
export abstract class BookingAction {
  /**
   * Führt die Buchung für eine Position aus. Von jeder Subklasse implementiert
   * und dort nach `targetType` weitergeleitet.
   *
   * @param position Zu buchende Position (article_id und amount)
   * @param params Buchungskontext, Adapter und stabile client_ids
   * @returns BookingResult mit success/message
   */
  public abstract execute(
    position: BookingPosition,
    params: BookingParams,
  ): Promise<BookingResult>;

  /**
   * Logging-Helfer für Subklassen - wickelt `Log.debug` ab, damit Tests
   * den Log-Output zentral mocken können.
   *
   * @param messages Beliebige Werte, die geloggt werden sollen
   */
  protected debugInfo(...messages: any[]): void {
    Log.debug('[action]', ...messages);
  }

  /**
   * Erzeugt ein erfolgreiches `BookingResult`.
   *
   * @param message Optionale Erfolgsmeldung
   * @returns BookingResult mit success: true
   */
  protected success(message: string = ''): BookingResult {
    return { success: true, message };
  }

  /**
   * Erzeugt ein Fehler-`BookingResult`.
   *
   * @param message Fehlermeldung mit Kontext, warum die Action fehlgeschlagen ist
   * @param retryable true bei transienten Ursachen (Netz/Lock): derselbe
   *   Request darf unverändert wiederholt werden (Default: false = permanent)
   * @returns BookingResult mit success: false
   */
  protected failure(message: string, retryable: boolean = false): BookingResult {
    return { success: false, message, retryable };
  }

  /**
   * Wenn ein neuer `StockTargetType` ergänzt wird, schreit der TypeScript-
   * Compiler hier wegen `never`-Default. So wird vergessenes Routing
   * Compile-Time gefangen.
   *
   * @param targetStock Nicht behandelter Ziel-Typ (Compile-Time `never`)
   * @returns Fehler-BookingResult mit dem unbehandelten Ziel-Typ
   */
  protected exhaustiveFailure(targetStock: never): BookingResult {
    return this.failure(`Unhandled target: ${targetStock}`);
  }

  /**
   * Reiht die Buchung für den späteren Auftrags-Rollback in die Transaction
   * ein, sofern eine `transactionId` gesetzt ist; ohne transactionId ein No-op.
   *
   * @param position Zu buchende Position (article_id und amount)
   * @param params Buchungskontext, Adapter und stabile client_ids
   * @returns null bei Erfolg (oder ohne Transaction); sonst das Fehler-Result,
   *   das die aufrufende Action unverändert zurückgeben soll
   */
  protected enqueueForRollback(
    position: BookingPosition,
    params: BookingParams,
  ): BookingResult | null {
    const { booking } = params.context;
    const { transactionId } = booking;
    if (transactionId === undefined || transactionId === null) {
      return null;
    }
    const enqueued = params.adapters.transaction.enqueueBooking(
      transactionId,
      position,
      booking,
    );
    if (!enqueued) {
      return this.failure(
        'Buchung konnte nicht in die Transaction enqueued werden',
      );
    }
    return null;
  }
}
